/**
 * HomepageEditor.jsx - CMS Editor for Homepage Sections
 * =====================================================
 * Manages all homepage content from Blueprint CMS:
 * - Hero Section (tagline, title, description, CTAs, image, search, badges)
 * - Features Section (6 feature cards)
 * - Journal Section (config for displaying recent posts)
 * - SRE Section (title, description, CTA, image)
 * - Stats Section (4 KPI cards)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import ImageEditorModal from './ImageEditorModal';
import supabase from '../../lib/supabase';
import { toast } from 'sonner';
import {
  Save, Loader2, Image as ImageIcon, Plus, Trash2, GripVertical,
  ChevronDown, ChevronUp, Eye, EyeOff, ExternalLink,
  Home, Layers, Newspaper, Box, BarChart3, Settings
} from 'lucide-react';

// Tab configuration
const TABS = [
  { id: 'hero', label: 'Hero', icon: Home },
  { id: 'features', label: 'Features', icon: Layers },
  { id: 'journal', label: 'Journal', icon: Newspaper },
  { id: 'sre', label: 'SRE', icon: Box },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
];

const HomepageEditor = ({ showToast }) => {
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [heroData, setHeroData] = useState(null);
  const [featuresData, setFeaturesData] = useState([]);
  const [journalData, setJournalData] = useState(null);
  const [sreData, setSreData] = useState(null);
  const [statsData, setStatsData] = useState([]);
  
  // Image editor modal
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [imageEditorConfig, setImageEditorConfig] = useState({
    initialImage: null,
    onSave: () => {},
    title: 'Editor Immagine'
  });

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load Hero
      const { data: hero } = await supabase
        .from('blueprint_homepage_hero')
        .select('*')
        .limit(1)
        .single();
      setHeroData(hero || {});

      // Load Features
      const { data: features } = await supabase
        .from('blueprint_homepage_features')
        .select('*')
        .order('order_index');
      setFeaturesData(features || []);

      // Load Journal config
      const { data: journal } = await supabase
        .from('blueprint_homepage_journal')
        .select('*')
        .limit(1)
        .single();
      setJournalData(journal || {});

      // Load SRE
      const { data: sre } = await supabase
        .from('blueprint_homepage_sre')
        .select('*')
        .limit(1)
        .single();
      setSreData(sre || {});

      // Load Stats
      const { data: stats } = await supabase
        .from('blueprint_homepage_stats')
        .select('*')
        .order('order_index');
      setStatsData(stats || []);

    } catch (err) {
      console.error('Error loading homepage data:', err);
      showToast?.('Errore caricamento dati', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save functions
  const saveHero = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_homepage_hero')
        .upsert({ ...heroData, updated_at: new Date().toISOString() });
      if (error) throw error;
      showToast?.('Hero salvato!');
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveFeature = async (feature) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_homepage_features')
        .upsert({ ...feature, updated_at: new Date().toISOString() });
      if (error) throw error;
      showToast?.('Feature salvata!');
      loadData();
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteFeature = async (id) => {
    if (!window.confirm('Eliminare questa feature?')) return;
    try {
      await supabase.from('blueprint_homepage_features').delete().eq('id', id);
      showToast?.('Feature eliminata');
      loadData();
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    }
  };

  const addFeature = async () => {
    const newFeature = {
      title_it: 'Nuova Feature',
      title_en: 'New Feature',
      description_it: 'Descrizione...',
      description_en: 'Description...',
      icon: 'Sparkles',
      link_url: '#',
      order_index: featuresData.length + 1,
      is_active: true
    };
    try {
      await supabase.from('blueprint_homepage_features').insert(newFeature);
      showToast?.('Feature aggiunta');
      loadData();
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    }
  };

  const saveJournal = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_homepage_journal')
        .upsert({ ...journalData, updated_at: new Date().toISOString() });
      if (error) throw error;
      showToast?.('Journal config salvato!');
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveSre = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_homepage_sre')
        .upsert({ ...sreData, updated_at: new Date().toISOString() });
      if (error) throw error;
      showToast?.('SRE salvato!');
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveStat = async (stat) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_homepage_stats')
        .upsert(stat);
      if (error) throw error;
      showToast?.('Stat salvata!');
      loadData();
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const addStat = async () => {
    const newStat = {
      value: '0+',
      label_it: 'Nuova statistica',
      label_en: 'New stat',
      icon: 'Trophy',
      order_index: statsData.length + 1,
      is_active: true
    };
    try {
      await supabase.from('blueprint_homepage_stats').insert(newStat);
      showToast?.('Stat aggiunta');
      loadData();
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    }
  };

  const deleteStat = async (id) => {
    if (!window.confirm('Eliminare questa statistica?')) return;
    try {
      await supabase.from('blueprint_homepage_stats').delete().eq('id', id);
      showToast?.('Stat eliminata');
      loadData();
    } catch (err) {
      showToast?.(`Errore: ${err.message}`, 'error');
    }
  };

  // Open image editor
  const openImageEditor = (currentImage, onSave, title = 'Editor Immagine') => {
    setImageEditorConfig({
      initialImage: currentImage,
      onSave,
      title
    });
    setImageEditorOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-4">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#B08B5C] text-black'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Hero Tab */}
      {activeTab === 'hero' && heroData && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left column */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Testi Hero</h3>
              
              <div>
                <Label className="text-white/60 text-xs">Tagline (IT)</Label>
                <Input
                  value={heroData.tagline_it || ''}
                  onChange={e => setHeroData({...heroData, tagline_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/60 text-xs">Titolo (IT) - usa *testo* per corsivo</Label>
                <Input
                  value={heroData.title_it || ''}
                  onChange={e => setHeroData({...heroData, title_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/60 text-xs">Descrizione (IT)</Label>
                <Textarea
                  value={heroData.description_it || ''}
                  onChange={e => setHeroData({...heroData, description_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/60 text-xs">CTA Primario Label</Label>
                  <Input
                    value={heroData.cta_primary_label_it || ''}
                    onChange={e => setHeroData({...heroData, cta_primary_label_it: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">CTA Primario URL</Label>
                  <Input
                    value={heroData.cta_primary_url || ''}
                    onChange={e => setHeroData({...heroData, cta_primary_url: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/60 text-xs">CTA Secondario Label</Label>
                  <Input
                    value={heroData.cta_secondary_label_it || ''}
                    onChange={e => setHeroData({...heroData, cta_secondary_label_it: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">CTA Secondario URL</Label>
                  <Input
                    value={heroData.cta_secondary_url || ''}
                    onChange={e => setHeroData({...heroData, cta_secondary_url: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/60 text-xs">Search Placeholder</Label>
                <Input
                  value={heroData.search_placeholder_it || ''}
                  onChange={e => setHeroData({...heroData, search_placeholder_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            {/* Right column - Image */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Immagine Hero</h3>
              
              <div 
                className="aspect-video bg-[#0a0a09] rounded-xl border border-white/10 overflow-hidden cursor-pointer hover:border-[#B08B5C] transition-colors"
                onClick={() => openImageEditor(
                  heroData.hero_image_url,
                  (url) => setHeroData({...heroData, hero_image_url: url}),
                  'Immagine Hero'
                )}
              >
                {heroData.hero_image_url ? (
                  <img 
                    src={heroData.hero_image_url} 
                    alt="Hero" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/30">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <p className="text-sm">Clicca per caricare</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-white/60 text-xs">Trust Badges (JSON)</Label>
                <Textarea
                  value={JSON.stringify(heroData.trust_badges || [], null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setHeroData({...heroData, trust_badges: parsed});
                    } catch {}
                  }}
                  className="bg-white/5 border-white/10 text-white font-mono text-xs min-h-[150px]"
                />
              </div>
            </div>
          </div>

          <Button 
            onClick={saveHero}
            disabled={saving}
            className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva Hero
          </Button>
        </div>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Feature Cards ({featuresData.length})</h3>
            <Button onClick={addFeature} size="sm" className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi
            </Button>
          </div>

          <div className="space-y-4">
            {featuresData.map((feature, idx) => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                onSave={saveFeature}
                onDelete={() => deleteFeature(feature.id)}
                onImageEdit={() => openImageEditor(
                  feature.image_url,
                  (url) => saveFeature({...feature, image_url: url}),
                  `Immagine Feature: ${feature.title_it}`
                )}
                saving={saving}
              />
            ))}
          </div>
        </div>
      )}

      {/* Journal Tab */}
      {activeTab === 'journal' && journalData && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white">Configurazione Sezione Journal</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-xs">Label Sezione (IT)</Label>
              <Input
                value={journalData.label_it || ''}
                onChange={e => setJournalData({...journalData, label_it: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Numero Post da mostrare</Label>
              <Input
                type="number"
                value={journalData.posts_count || 4}
                onChange={e => setJournalData({...journalData, posts_count: parseInt(e.target.value)})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-xs">Titolo (IT)</Label>
            <Input
              value={journalData.title_it || ''}
              onChange={e => setJournalData({...journalData, title_it: e.target.value})}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <Label className="text-white/60 text-xs">Sottotitolo (IT)</Label>
            <Textarea
              value={journalData.subtitle_it || ''}
              onChange={e => setJournalData({...journalData, subtitle_it: e.target.value})}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-xs">Link "Vedi tutti" Label</Label>
              <Input
                value={journalData.view_all_label_it || ''}
                onChange={e => setJournalData({...journalData, view_all_label_it: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Link "Vedi tutti" URL</Label>
              <Input
                value={journalData.view_all_url || ''}
                onChange={e => setJournalData({...journalData, view_all_url: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>

          <p className="text-white/40 text-sm">
            ℹ️ Gli articoli vengono caricati automaticamente dalla tabella <code>blueprint_posts</code> 
            (i {journalData.posts_count || 4} più recenti con status "published").
          </p>

          <Button 
            onClick={saveJournal}
            disabled={saving}
            className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva Journal Config
          </Button>
        </div>
      )}

      {/* SRE Tab */}
      {activeTab === 'sre' && sreData && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Sezione SRE</h3>
              
              <div>
                <Label className="text-white/60 text-xs">Label (IT)</Label>
                <Input
                  value={sreData.label_it || ''}
                  onChange={e => setSreData({...sreData, label_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/60 text-xs">Titolo (IT)</Label>
                <Input
                  value={sreData.title_it || ''}
                  onChange={e => setSreData({...sreData, title_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/60 text-xs">Descrizione (IT)</Label>
                <Textarea
                  value={sreData.description_it || ''}
                  onChange={e => setSreData({...sreData, description_it: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/60 text-xs">CTA Label</Label>
                  <Input
                    value={sreData.cta_label_it || ''}
                    onChange={e => setSreData({...sreData, cta_label_it: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">CTA URL</Label>
                  <Input
                    value={sreData.cta_url || ''}
                    onChange={e => setSreData({...sreData, cta_url: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Immagine SRE</h3>
              
              <div 
                className="aspect-video bg-[#0a0a09] rounded-xl border border-white/10 overflow-hidden cursor-pointer hover:border-[#B08B5C] transition-colors"
                onClick={() => openImageEditor(
                  sreData.image_url,
                  (url) => setSreData({...sreData, image_url: url}),
                  'Immagine SRE'
                )}
              >
                {sreData.image_url ? (
                  <img 
                    src={sreData.image_url} 
                    alt="SRE" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/30">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <p className="text-sm">Clicca per caricare</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button 
            onClick={saveSre}
            disabled={saving}
            className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva SRE
          </Button>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Statistiche KPI ({statsData.length})</h3>
            <Button onClick={addStat} size="sm" className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {statsData.map((stat) => (
              <StatCard
                key={stat.id}
                stat={stat}
                onSave={saveStat}
                onDelete={() => deleteStat(stat.id)}
                saving={saving}
              />
            ))}
          </div>
        </div>
      )}

      {/* Image Editor Modal */}
      <ImageEditorModal
        isOpen={imageEditorOpen}
        onClose={() => setImageEditorOpen(false)}
        onSave={imageEditorConfig.onSave}
        initialImage={imageEditorConfig.initialImage}
        title={imageEditorConfig.title}
      />
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ feature, onSave, onDelete, onImageEdit, saving }) => {
  const [expanded, setExpanded] = useState(false);
  const [localData, setLocalData] = useState(feature);

  useEffect(() => {
    setLocalData(feature);
  }, [feature]);

  return (
    <div className="bg-[#1a1a18] rounded-xl border border-white/10 overflow-hidden">
      <div 
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical className="w-4 h-4 text-white/30" />
        <div className="w-12 h-12 bg-[#0a0a09] rounded-lg overflow-hidden flex-shrink-0">
          {localData.image_url ? (
            <img src={localData.image_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-white/20" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <h4 className="text-white font-medium">{localData.title_it}</h4>
          <p className="text-white/40 text-sm truncate">{localData.description_it}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs ${localData.is_active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}>
            {localData.is_active ? 'Attivo' : 'Nascosto'}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-t border-white/10 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-xs">Titolo (IT)</Label>
              <Input
                value={localData.title_it || ''}
                onChange={e => setLocalData({...localData, title_it: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Icona (Lucide)</Label>
              <Input
                value={localData.icon || ''}
                onChange={e => setLocalData({...localData, icon: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Search, Users, Box..."
              />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-xs">Descrizione (IT)</Label>
            <Textarea
              value={localData.description_it || ''}
              onChange={e => setLocalData({...localData, description_it: e.target.value})}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-xs">Link URL</Label>
              <Input
                value={localData.link_url || ''}
                onChange={e => setLocalData({...localData, link_url: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Ordine</Label>
              <Input
                type="number"
                value={localData.order_index || 0}
                onChange={e => setLocalData({...localData, order_index: parseInt(e.target.value)})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onImageEdit(); }}
              className="border-white/20 text-white/70"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              {localData.image_url ? 'Cambia immagine' : 'Carica immagine'}
            </Button>
            
            <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={localData.is_active}
                onChange={e => setLocalData({...localData, is_active: e.target.checked})}
                className="rounded"
              />
              Visibile
            </label>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-white/10">
            <Button
              size="sm"
              onClick={() => onSave(localData)}
              disabled={saving}
              className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ stat, onSave, onDelete, saving }) => {
  const [localData, setLocalData] = useState(stat);

  useEffect(() => {
    setLocalData(stat);
  }, [stat]);

  return (
    <div className="bg-[#1a1a18] rounded-xl border border-white/10 p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-white/60 text-xs">Valore</Label>
          <Input
            value={localData.value || ''}
            onChange={e => setLocalData({...localData, value: e.target.value})}
            className="bg-white/5 border-white/10 text-white text-lg font-bold"
            placeholder="10.000+"
          />
        </div>
        <div>
          <Label className="text-white/60 text-xs">Icona (Lucide)</Label>
          <Input
            value={localData.icon || ''}
            onChange={e => setLocalData({...localData, icon: e.target.value})}
            className="bg-white/5 border-white/10 text-white"
            placeholder="Trophy, Users, Globe..."
          />
        </div>
      </div>

      <div>
        <Label className="text-white/60 text-xs">Label (IT)</Label>
        <Input
          value={localData.label_it || ''}
          onChange={e => setLocalData({...localData, label_it: e.target.value})}
          className="bg-white/5 border-white/10 text-white"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => onSave(localData)}
          disabled={saving}
          className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black flex-1"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salva
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default HomepageEditor;
